import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input, Skeleton, toast } from "@picklebet/ui";
import { parseError } from "utilities/errorsParser";
import { useDepositWizard } from "features/wallet/components/deposit/hooks/useDepositWizard";
import { useCompleteDeposit } from "features/wallet/components/deposit/hooks/useCompleteDeposit";
import { useDepositAmountValidationSchema } from "features/wallet/hooks/useDepositAmountValidationSchema";
import { useReportSubmitting } from "features/wallet/hooks/useReportSubmitting";
import { useSelectedCard } from "features/wallet/components/deposit/hooks/useSelectedCard";
import { DepositAmountInput } from "../../blocks/deposit-amount-input/DepositAmountInput";
import { DepositMethodField } from "../../blocks/deposit-method-field/DepositMethodField";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";
import {
  DepositContent,
  DepositContentSkeleton,
} from "../../blocks/deposit-content/DepositContent";
import { DebitCardDepositAction } from "../../debit-card-deposit-action/DebitCardDepositAction";
import * as styles from "./DebitCardPrimaryView.module.scss";
import { usePendingDepositClaim } from "hooks/firestore/user/usePromotionClaims";
import { walletService } from "features/wallet/services/wallet";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { MethodRowSkeleton } from "../../method-row/MethodRow";

export const DebitCardPrimaryViewSkeleton = () => {
  return (
    <DepositContentSkeleton>
      <AmountInputSkeleton />

      <SectionDivider />

      <SectionHeader>Payment Method</SectionHeader>

      <MethodRowSkeleton />

      <Skeleton className={styles.buttonSkeleton} />
    </DepositContentSkeleton>
  );
};

export const DebitCardPrimaryView = () => {
  const { amount, setAmount, setSubmitting } = useDepositWizard();
  const completeDeposit = useCompleteDeposit();
  const { selectedCard, isLoading: cardsLoading } = useSelectedCard();
  const { pendingDepositClaim, isLoading: claimLoading } =
    usePendingDepositClaim();
  const [amountSchema] = useDepositAmountValidationSchema();

  const requiresCSC = useMemo(() => {
    return (
      selectedCard &&
      (typeof selectedCard.successfulDepositCount === "undefined" ||
        selectedCard.successfulDepositCount === 0)
    );
  }, [selectedCard]);

  const formSchema = useMemo(() => {
    if (!amountSchema) return;

    return amountSchema
      .extend({ csc: z.string().optional() })
      .superRefine((values, ctx) => {
        if (requiresCSC) {
          if (!values.csc) {
            ctx.addIssue({
              code: "custom",
              path: ["csc"],
              message: "CSC should not be empty",
            });
            return;
          }

          if (values.csc.length < 3 || values.csc.length > 4) {
            ctx.addIssue({
              code: "custom",
              path: ["csc"],
              message: "CSC should be 3 or 4 characters long",
            });
            return;
          }
        }
      });
  }, [amountSchema, requiresCSC]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: formSchema ? zodResolver(formSchema) : undefined,
    defaultValues: {
      amount,
    },
  });

  useReportSubmitting(isSubmitting, setSubmitting);

  const onSubmit = async (values: { amount: number; csc?: string }) => {
    const promotionCode = pendingDepositClaim
      ? pendingDepositClaim?.code
      : undefined;

    const csc = values.csc === "" ? undefined : values.csc;

    const result = await walletService.merchantWarriorDeposit({
      cardId: selectedCard?.id,
      csc,
      amount: values.amount,
      promotionCode,
    });

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Deposit Failed",
        description: parseError(result.error),
      });
      return;
    }

    if (result.data === "not-enrolled") {
      toast({
        variant: "danger",
        title: "Deposit Failed",
        description:
          "Your card could not be enrolled against our 3D Secure 2 verification. Please try another card or one of the other payment methods.",
      });
      return;
    }

    if (result.data === "success") {
      completeDeposit();
    }
  };

  if (claimLoading || cardsLoading) {
    return <DebitCardPrimaryViewSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DepositContent>
        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value } }) => (
            <DepositAmountInput
              value={value}
              onChange={(next) => {
                onChange(next);
                setAmount(next);
              }}
              errorMessage={errors.amount?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <SectionDivider />

        <SectionHeader>Payment Method</SectionHeader>

        <DepositMethodField />

        {requiresCSC && (
          <Controller
            control={control}
            name="csc"
            shouldUnregister
            render={({ field }) => (
              <Input
                label="CSC"
                placeholder="3 or 4 digits on the back of your card"
                maxLength={4}
                errorMessage={errors?.csc?.message}
                inputMode={"numeric"}
                disabled={isSubmitting}
                {...field}
              />
            )}
          />
        )}

        <div className={styles.buttonContainer}>
          <DebitCardDepositAction loading={isSubmitting} />
        </div>
      </DepositContent>
    </form>
  );
};
